import { useCallback, useRef } from 'react';

export type ResizeDirection = 'left' | 'right';
export type ResizeCallback = (direction: ResizeDirection, width: number) => void;

/**
 * 面板拖拽Hook - 优化版本
 * 使用基于偏移量的计算，提升拖动灵敏度
 */
export const usePanelResize = (
  sidebarWidth: number,
  onResize?: ResizeCallback
) => {
  const onResizeRef = useRef(onResize);
  onResizeRef.current = onResize;

  // 记录拖动开始时的面板宽度
  const startWidthRef = useRef<{ left: number; right: number }>({ left: 280, right: 400 });

  const setStartWidths = useCallback((leftWidth: number, rightWidth: number) => {
    startWidthRef.current = { left: leftWidth, right: rightWidth };
  }, []);

  // 左侧面板调整 - 基于偏移量计算
  const handleLeftResize = useCallback((e: MouseEvent, delta: number) => {
    if (e.clientX == null || isNaN(e.clientX)) return;

    // 基于起始宽度加上偏移量计算新宽度
    const newWidth = startWidthRef.current.left + delta;
    const clampedWidth = Math.max(200, Math.min(600, newWidth));

    // 直接同步调用，不使用 requestAnimationFrame 节流
    onResizeRef.current?.('left', clampedWidth);

    return clampedWidth;
  }, []);

  // 右侧面板调整 - 基于偏移量计算
  const handleRightResize = useCallback((e: MouseEvent, delta: number) => {
    if (e.clientX == null || isNaN(e.clientX)) return;

    // 右侧面板：向左拖动（负delta）应该增加宽度，向右拖动（正delta）应该减小宽度
    const newWidth = startWidthRef.current.right - delta;
    const clampedWidth = Math.max(300, Math.min(800, newWidth));

    // 直接同步调用，不使用 requestAnimationFrame 节流
    onResizeRef.current?.('right', clampedWidth);

    return clampedWidth;
  }, []);

  // 清理函数（保留以兼容现有API，但不再需要取消动画帧）
  const cleanup = useCallback(() => {
    // 不再需要清理动画帧
  }, []);

  return {
    handleLeftResize,
    handleRightResize,
    setStartWidths,
    cleanup,
  };
};